import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Users, Globe, Zap, PlayCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import AnimatedCounter from '@/components/AnimatedCounter';
import GlassBoxBadge from '@/components/GlassBoxBadge';
import Footer from '@/components/Footer';

const HERO_BG = '/assets/hero.jpg';
const COMMUNITY_IMG = '/assets/community.jpg';
const STORIES_BG = '/assets/stories.jpg';
const LOGO_URL = '/assets/logo.jpg';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

function WelcomeTopBar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-xl border-b border-gray-100" data-testid="welcome-topbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
        <Link to="/welcome" className="flex items-center gap-2">
          <img src={LOGO_URL} alt="HumaClub" className="h-7 w-auto rounded" />
          <span className="font-semibold text-lg text-[#111827]">HumaClub</span>
        </Link>
        <div className="hidden md:flex items-center gap-6 text-sm font-medium text-gray-600">
          <Link to="/explore" className="hover:text-[#7C3AED] transition-colors">Explore</Link>
          <Link to="/principles" className="hover:text-[#7C3AED] transition-colors">Glass Box</Link>
          <Link to="/volunteer" className="hover:text-[#7C3AED] transition-colors">Volunteer</Link>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/signin"><Button variant="ghost" size="sm" className="rounded-full text-sm text-gray-600" data-testid="welcome-signin">Sign In</Button></Link>
          <Link to="/signup"><Button size="sm" className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white text-sm rounded-full border-0 px-5" data-testid="welcome-join">Join</Button></Link>
        </div>
      </div>
    </nav>
  );
}

function mockFeedPreview() {
  return [
    { type: 'ngo', name: 'Station House BBQ Impact', text: 'We just completed our 2,000th meal delivery this month!', metric: '247 meals this week', time: '2h', avatar: 'https://images.unsplash.com/photo-1504674900247-0877df9cc836?w=200&h=200&fit=crop' },
    { type: 'donation', name: 'Sarah Chen', text: '$500 donated to 3,000 Meals for Tampa Bay', metric: 'On-chain verified', time: '4h', avatar: 'https://api.dicebear.com/7.x/avataaars/svg?seed=0' },
    { type: 'milestone', name: 'Tampa Bay Outreach', text: 'Milestone 3: 2,000 of 3,000 meals delivered!', metric: '67% complete', time: '6h', avatar: 'https://images.unsplash.com/photo-1469571486292-0ba58a3f068b?w=200&h=200&fit=crop' },
  ];
}

export default function WelcomePage() {
  const [stats, setStats] = useState(null);
  const [donors, setDonors] = useState([]);

  useEffect(() => {
    setStats({ totalDonations: 847293, mealsServed: 3247, ngoPartners: 127, volunteerHours: 14823 });
    fetch('/data/donors.json').then(r => r.json()).then(d => setDonors(d.slice(0, 8))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="welcome-page">
      <WelcomeTopBar />

      {/* HERO */}
      <section className="pt-14">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
          <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
            <motion.div className="lg:w-1/2" initial="hidden" animate="visible" variants={fadeUp} transition={{ duration: 0.6 }}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] tracking-tight leading-tight" data-testid="hero-headline">
                Humanity, <br /><span className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] bg-clip-text text-transparent">Verifiable.</span>
              </h1>
              <p className="mt-5 text-lg text-gray-500 leading-relaxed max-w-lg">
                The social network where every dollar, every hour, every impact is proven — not promised.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 mt-8">
                <Link to="/signup">
                  <Button className="bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white rounded-full px-8 py-3 text-base font-medium h-auto border-0 hover:shadow-xl hover:-translate-y-0.5 transition-all" data-testid="hero-join-cta">
                    Join the Network <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
                <Link to="/explore">
                  <Button variant="outline" className="rounded-full px-8 py-3 text-base font-medium border-gray-200 h-auto" data-testid="hero-explore-cta">
                    Explore as Guest
                  </Button>
                </Link>
              </div>
            </motion.div>

            {/* Feed Preview */}
            <motion.div className="lg:w-1/2 w-full max-w-md" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.7, delay: 0.2 }}>
              <div className="bg-[#F8F9FA] rounded-2xl border border-gray-200 p-4 shadow-2xl shadow-gray-200/50" data-testid="feed-preview">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-100">
                  <div className="w-3 h-3 rounded-full bg-red-400" />
                  <div className="w-3 h-3 rounded-full bg-yellow-400" />
                  <div className="w-3 h-3 rounded-full bg-green-400" />
                  <span className="text-xs text-gray-300 ml-2">HumaClub Feed</span>
                </div>
                <div className="space-y-3">
                  {mockFeedPreview().map((item) => (
                    <motion.div key={item.name} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 + 0.15 }} className="bg-white rounded-xl p-3 border border-gray-100">
                      <div className="flex items-center gap-2 mb-1.5">
                        <img src={item.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />
                        <span className="text-xs font-semibold text-[#111827]">{item.name}</span>
                        <ShieldCheck className="w-3 h-3 text-emerald-500" />
                        <span className="text-[10px] text-gray-300 ml-auto">{item.time}</span>
                      </div>
                      <p className="text-xs text-gray-600 mb-1.5">{item.text}</p>
                      <div className="flex items-center gap-1 text-[10px] text-emerald-600 font-medium">
                        <ShieldCheck className="w-2.5 h-2.5" />{item.metric}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ANIMATED COUNTER STRIP */}
      {stats && (
        <section className="border-y border-gray-100 bg-[#FAFAFA] py-6 overflow-hidden" data-testid="counter-strip">
          <div className="max-w-5xl mx-auto px-4 flex items-center justify-between gap-4 flex-wrap">
            {[
              { value: stats.totalDonations, prefix: '$', label: 'verified donations' },
              { value: stats.mealsServed, label: 'meals served' },
              { value: stats.ngoPartners, label: 'NGOs' },
              { value: stats.volunteerHours, label: 'volunteer hours' },
            ].map((s) => (
              <div key={s.label} className="flex items-center gap-2 text-center sm:text-left">
                <span className="text-xl sm:text-2xl font-bold text-[#111827]">
                  {s.prefix}<AnimatedCounter end={s.value} duration={2} />
                </span>
                <span className="text-xs text-gray-400 font-medium">{s.label}</span>
                <GlassBoxBadge />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SOCIAL PROOF */}
      <section className="py-8 bg-white" data-testid="social-proof">
        <div className="max-w-5xl mx-auto px-4 flex items-center justify-center gap-3">
          <div className="flex items-center">
            {donors.slice(0, 6).map((d) => (
              <img key={d.id || d.name} src={d.avatar} alt="" className="w-8 h-8 rounded-full border-2 border-white -ml-2 first:ml-0" />
            ))}
          </div>
          <span className="text-sm text-gray-500 font-medium">+14,823 changemakers already on HumaClub</span>
        </div>
      </section>

      {/* 3-COLUMN FEATURES */}
      <section className="py-20 bg-white" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp} transition={{ duration: 0.5 }}>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] tracking-tight">A social network built on truth</h2>
            <p className="mt-3 text-gray-500 max-w-lg mx-auto">No vanity metrics. No greenwashing. Every number on HumaClub is verified on-chain.</p>
          </motion.div>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              { title: 'Your Impact Feed', desc: 'See real-time verified updates from NGOs, donors, and volunteers — every action tracked and proven.', icon: Zap, img: HERO_BG },
              { title: 'Verified Everything', desc: 'Glass Box Verified badges on every donation, milestone, and report. Click any badge to see the on-chain proof.', icon: ShieldCheck, img: STORIES_BG },
              { title: 'Communities That Care', desc: 'Join cause-based communities, celebrate milestones together, and amplify the impact that matters to you.', icon: Users, img: COMMUNITY_IMG },
            ].map((f) => (
              <motion.div key={f.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="rounded-2xl overflow-hidden border border-gray-100 group hover:shadow-lg transition-shadow" data-testid={`feature-card-${f.title}`}>
                <div className="h-44 overflow-hidden">
                  <img src={f.img} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5">
                  <div className="flex items-center gap-2 mb-2">
                    <f.icon className="w-5 h-5 text-[#7C3AED]" />
                    <h3 className="font-semibold text-[#111827]">{f.title}</h3>
                  </div>
                  <p className="text-sm text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 bg-[#FAFAFA]" data-testid="how-it-works-welcome">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-[#111827] tracking-tight mb-12">How HumaClub Works</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { step: '01', label: 'Join', desc: 'Create your profile as a donor, NGO, volunteer, or corporate partner.' },
              { step: '02', label: 'Connect', desc: 'Discover causes, NGOs, and communities aligned with your values.' },
              { step: '03', label: 'Give / Volunteer', desc: 'Donate to verified projects or log volunteer hours — all tracked.' },
              { step: '04', label: 'See Impact', desc: 'Every dollar and hour is verified on-chain with Glass Box badges.' },
            ].map((s) => (
              <motion.div key={s.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="text-center" data-testid={`step-${s.step}`}>
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-[#7C3AED] to-[#2563EB] flex items-center justify-center mx-auto mb-3">
                  <span className="text-white font-bold text-lg">{s.step}</span>
                </div>
                <h3 className="font-semibold text-[#111827] mb-1">{s.label}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL */}
      <section className="py-20 bg-white" data-testid="testimonial-section">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <blockquote className="text-xl sm:text-2xl text-[#111827] italic leading-relaxed font-light">
            "HumaClub didn't just help us raise funds — it proved to our donors that every dollar they gave actually reached the families we serve. That trust changes everything."
          </blockquote>
          <div className="mt-6 flex items-center justify-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200" />
            <div className="text-left">
              <p className="text-sm font-semibold text-[#111827]">Sarah Mitchell</p>
              <p className="text-xs text-gray-400">Community Advocate</p>
            </div>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 bg-gradient-to-r from-[#7C3AED] to-[#2563EB] text-white text-center" data-testid="final-cta">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">Ready to make verified impact?</h2>
          <p className="text-white/70 mb-8">Join thousands of changemakers building a more transparent world.</p>
          <Link to="/signup">
            <Button className="bg-white text-[#7C3AED] rounded-full px-8 py-3 text-base font-semibold h-auto hover:bg-gray-100 transition-colors border-0" data-testid="final-join-cta">
              Join HumaClub <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
